import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  withHandlers,
  lifecycle
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { addGroupAccountAdmin } from '../../actions/Accounts';
import BackButton from '../../components/atoms/BackButton';
import RegisterButton from '../../components/atoms/RegisterButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import ImagePreview from '../../components/molecules/ImagePreview';
import { addGroupImage } from '../../actions/Groups';
import {
  getCurrentUser,
  setPreferredMFA,
  sendLogin,
  resizeImage,
  uploadFile
} from '../../auth/Auth';

const styles = {
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none'
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px'
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  description: {
    color: '#707070',
    margin: '20px 0px 0px'
  },
  subTitle: {
    margin: '20px 0px 0px',
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0px'
  },
  detailTable: {
    width: 'auto'
  },
  tableCellItem: {
    border: '2px solid #ffffff',
    backgroundColor: '#e0e0e0'
  },
  tableCellData: {
    border: '2px solid #ffffff'
  },
  tableCell90: {
    width: '90%'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px'
  },
  editTable: {
    width: 'auto'
  },
  tableCell: {
    border: '2px solid #ffffff'
  },
  caution: {
    color: 'red',
    fontSize: '1.1rem'
  },
  breakLines: {
    whiteSpace: 'break-spaces'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      prefectures: state.commons.prefectures
    }),
    dispatch => ({
      actions: bindActionCreators(
        { addGroupAccountAdmin, addGroupImage },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openGroupAddProgress', 'updateOpenGroupAddProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isGroupAddSuccess', 'updateIsGroupAddSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('prefecture', 'setPrefecture', 0),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {}
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    openGroupAddProgress,
    updateOpenGroupAddProgress,
    loading,
    updateLoading,
    isGroupAddSuccess,
    updateIsGroupAddSuccess,
    resultMessage,
    setResultMessage,
    handleRedirect,
    backAction,
    addConfirm,
    addParam,
    prefectures,
    updateOpenGroupAddConfirm,
    updateOpenGroupAdd,
    updateGroups
  }) => {
    const registerAction = async () => {
      updateLoading(true);
      updateOpenGroupAddProgress(true);
      getCurrentUser()
        .then(async user => {
          await setPreferredMFA(user, 'NOMFA');
          const addedGroup = await actions
            .addGroupAccountAdmin(addParam)
            .then(data => checkSession(data));

          // upload image
          const imagePaths = await Promise.all(
            Object.keys(addParam.images).map(async i => {
              if (addParam.images[i].image !== null) {
                let { image } = addParam.images[i];
                if (image.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
                  const { resizeImageFile } = await resizeImage(image);
                  image = resizeImageFile;
                }
                const imagePath = await uploadFile(
                  'group',
                  addedGroup.payload.groupId,
                  image,
                  'public'
                );
                return imagePath;
              }
              return null;
            })
          );
          const images = imagePaths
            .filter(i => i !== null)
            .map((i, index) => {
              return { image: i, viewOrder: index };
            });
          if (Object.keys(images).length !== 0) {
            await actions
              .addGroupImage(addedGroup.payload.groupId, {
                images
              })
              .then(data => checkSession(data));
          }
          if (addedGroup.type === 'ADD_GROUP_ACCOUNT_SUCCESS') {
            updateIsGroupAddSuccess(true);
            updateOpenGroupAdd(false);
            updateOpenGroupAddConfirm(false);
            updateGroups(true);
            setResultMessage(
              '登録が完了しました。登録したメールアドレス/パスワードでログインしてください。'
            );
            updateLoading(false);
          } else {
            updateIsGroupAddSuccess(false);
            setResultMessage('登録に失敗しました。');
            updateLoading(false);
          }
        })
        .catch(error => {
          console.log('error', error);
          updateIsGroupAddSuccess(false);
          setResultMessage('システムエラーが発生しました。');
          updateLoading(false);
        });
    };

    const progressOkAction = () => {
      if (isGroupAddSuccess) {
        updateOpenGroupAddConfirm(false);
        updateOpenGroupAddProgress(false);
        handleRedirect('/');
      } else {
        updateOpenGroupAddProgress(false);
      }
    };

    const groupDisplayLimit =
      addConfirm.groupType === 'CAMPAIGN' ||
      addConfirm.groupType === 'OTHERS' ||
      addConfirm.groupType === 'GOCHI_KID';

    return (
      <div className={classes.root}>
        <Typography variant="h6">グループ運営会社登録申込書</Typography>
        <Paper className={classes.mainPaper} elevation={1}>
          <Table className={classes.detailTable}>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                貴グループ運営会社名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>郵便番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.zipCode}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>都道府県</TableCell>
              <TableCell className={classes.tableCellData}>
                {prefectures.map(item => {
                  if (
                    addConfirm.prefectureCode.toString() === item.id.toString()
                  ) {
                    return item.name;
                  }
                  return '';
                })}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>市区町村</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.city}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                町域・番地
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.address}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>建物名</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.building}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>電話番号</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.phone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>FAX</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.fax}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                ご担当者名
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupContactName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>所属部署</TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.department}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                電話番号（直通等）
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.directPhone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                インボイス制度 適格事業者登録
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.registrationId ? '登録済み' : '未登録'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                適格事業者登録番号
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.registrationId}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>画像</TableCell>
              <TableCell className={classes.tableCellData}>
                <ImagePreview previewImage={addConfirm.previewImage1} />
                <ImagePreview previewImage={addConfirm.previewImage2} />
                <ImagePreview previewImage={addConfirm.previewImage3} />
                <ImagePreview previewImage={addConfirm.previewImage4} />
                <ImagePreview previewImage={addConfirm.previewImage5} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループの概要
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.description}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                WebページURL
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.website}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ種別
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.groupTypeStr}
              </TableCell>
            </TableRow>
            {!groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  入金管理
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.depositManageStr}
                </TableCell>
              </TableRow>
            )}
            {/* {addConfirm.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし現金授受の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.cashAllowed ? '受け付ける' : '受け付けない'}
                </TableCell>
              </TableRow>
            )} */}
            {addConfirm.groupType !== 'OTHERS' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  社食ごちめし金額指定チケット利用の有無
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.allowMenuPriceShashoku
                    ? '利用する'
                    : '利用しない'}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>銀行名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankName}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>支店名</TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankBranchName}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座種別
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountTypeStr}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座番号
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountNumber}
                </TableCell>
              </TableRow>
            )}
            {addParam.depositManage === 'NONSELF' && !groupDisplayLimit && (
              <TableRow>
                <TableCell className={classes.tableCellItem}>
                  口座名義
                </TableCell>
                <TableCell className={classes.tableCellData}>
                  {addConfirm.bankAccountHolder}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                グループ紐付け時のメッセージ
              </TableCell>
              <TableCell
                className={`${classes.tableCellData} ${classes.breakLines}`}
              >
                {addConfirm.linkingShopMessage}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className={classes.tableCellItem}>
                メールアドレス
              </TableCell>
              <TableCell className={classes.tableCellData}>
                {addConfirm.email}
              </TableCell>
            </TableRow>
          </Table>
        </Paper>
        <div>
          <BackButton onClickAction={backAction} />
          <RegisterButton onClickAction={registerAction} />
        </div>
        <ProgressDialog
          openProgress={openGroupAddProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
